import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { Scene } from '@babylonjs/core/scene';
import { eMeshChangeTypes, Feature } from '@kb3d/models/classes/features/feature';
import { CustomMeshNode } from '@kb3d/models/classes/meshes/custom-mesh-node';
import { MeshNode } from '@kb3d/models/classes/meshes/mesh-node';
import { SpaceNode } from '@kb3d/models/classes/meshes/space-node';
import { Token } from '@kb3d/models/classes/token';
import { isTrackedClass, ITrackedClass } from '@kb3d/models/interfaces/tracked-class';
import { SubmeshVertexData, verifyGeometry } from '@kb3d/view/helpers/geometry-helper';
import { KbViewer } from '@kb3d/view/kb-viewer';
import { calculateBaseSubmeshes, FeatureOrderManager } from './feature-stack-common';
import { NormalSmoothingRenderer } from './features';
import { AmbientOcclusionRenderer } from './features/ambient-occlusion';
import { CircularPatternRenderer } from './features/circular-pattern';
import { DeleteFacesRenderer } from './features/delete-faces';
import { ChamferFeatureRenderer } from './features/chamfer';
import { DisplacementMapRenderer } from './features/displacement-map';
import { ExtrudeRenderer } from './features/extrude';
import { FeatureRenderer } from './features/feature';
import { JoinGeometryRenderer } from './features/join-geometry';
import { LinearPatternRenderer } from './features/linear-pattern';
import { MirrorFeatureRenderer } from './features/mirror';
import { MoveVerticesRenderer } from './features/move-vertices';
import { SliceFeatureRenderer } from './features/slice';
import { SubdivisionFeatureRenderer } from './features/subdivision';
import { SubmeshRenderer } from './features/submesh';
import { SweepRenderer } from './features/sweep';
import { TransformRenderer } from './features/transform';
import { UvMapRenderer } from './features/uvmap';
import { WeldVerticesRenderer } from './features/weld-vertices';
import { Renderer } from './renderer';
import { RendererManager } from './renderer-manager';
import { MergePolygonsRenderer } from './features/merge-polygons';

export class FeatureStackRenderer extends Renderer<Feature, Mesh> {
    queuedGeometryToReset = new Map<string, Partial<SubmeshVertexData>>();
    queuedFeaturesToProcess = new Map<SpaceNode, Set<number>>(); // index of feature to start recalculation at
    processedFeatures = new Map<string, boolean>();
    featureOrder = new FeatureOrderManager();
    renderers = new Map<Token, FeatureRenderer<any>>();
    enabled = false;

    processedGeometryMap = new Map<string, SubmeshVertexData>();

    constructor(viewer: KbViewer, scene: Scene, private rendererManager: RendererManager) {
        super(viewer, scene);
        const moveVertices = new MoveVerticesRenderer(viewer, scene);
        this.renderers.set(Token.MoveVerticesFeature, moveVertices);
        const weldVertices = new WeldVerticesRenderer(viewer, scene);
        this.renderers.set(Token.WeldVerticesFeature, weldVertices);
        const displacementMap = new DisplacementMapRenderer(viewer, scene);
        this.renderers.set(Token.DisplacementMapFeature, displacementMap);
        const uvMaps = new UvMapRenderer(viewer, scene);
        this.renderers.set(Token.UvMapFeature, uvMaps);
        const submeshes = new SubmeshRenderer(viewer, scene, rendererManager);
        this.renderers.set(Token.SubmeshFeature, submeshes);
        const linearPatterns = new LinearPatternRenderer(viewer, scene);
        this.renderers.set(Token.LinearPatternFeature, linearPatterns);
        const circularPatterns = new CircularPatternRenderer(viewer, scene);
        this.renderers.set(Token.CircularPatternFeature, circularPatterns);
        const joinGeometry = new JoinGeometryRenderer(viewer, scene);
        this.renderers.set(Token.JoinGeometryFeature, joinGeometry);
        const deleteFaces = new DeleteFacesRenderer(viewer, scene);
        this.renderers.set(Token.DeleteFacesFeature, deleteFaces);
        const mergePolygons = new MergePolygonsRenderer(viewer, scene);
        this.renderers.set(Token.MergePolygonsFeature, mergePolygons);
        const chamfer = new ChamferFeatureRenderer(viewer, scene);
        this.renderers.set(Token.ChamferFeature, chamfer);
        const normalSmoothing = new NormalSmoothingRenderer(viewer, scene);
        this.renderers.set(Token.NormalSmoothingFeature, normalSmoothing);
        const slice = new SliceFeatureRenderer(viewer, scene);
        this.renderers.set(Token.SliceFeature, slice);
        const subdivision = new SubdivisionFeatureRenderer(viewer, scene);
        this.renderers.set(Token.SubdivisionFeature, subdivision);
        const mirror = new MirrorFeatureRenderer(viewer, scene);
        this.renderers.set(Token.MirrorFeature, mirror);
        const transform = new TransformRenderer(viewer, scene);
        this.renderers.set(Token.TransformFeature, transform);
        const extrude = new ExtrudeRenderer(viewer, scene);
        this.renderers.set(Token.ExtrudeFeature, extrude);
        const sweep = new SweepRenderer(viewer, scene);
        this.renderers.set(Token.SweepFeature, sweep);
        const ambientOcclusion = new AmbientOcclusionRenderer(viewer, scene);
        this.renderers.set(Token.AmbientOcclusionFeature, ambientOcclusion);
    }

    modelToken = Token.Feature;

    forceRebuildGeometry(meshNode: MeshNode) {
        if (!this.enabled) {
            return;
        }
        const vertexData = this.rendererManager.mesh.rebuildBaseGeometry(meshNode)!;
        this.queuedGeometryToReset.set(meshNode._dynamicId, vertexData);
        const allChanges = new Set(meshNode.features.map((_, i) => i));
        this.queuedFeaturesToProcess.set(meshNode, allChanges);

        const movedIndex = this.featureOrder.getMovedFeatureIndex(meshNode);
        this.featureOrder.updateFeatureOrder(meshNode);
        if (movedIndex !== undefined && movedIndex >= 0) {
            this.featureOrder.recursivelyUpdateFeatureVisibility(
                [meshNode.features[movedIndex]],
                meshNode.features,
                []
            );
        }
        this.update3dGeometry(0, meshNode);

        this.renderers.forEach(renderer => {
            renderer.clearCache(meshNode.features);
        });
    }

    resetMeshGeometry(
        meshNode: SpaceNode,
        dataTypes: Set<eMeshChangeTypes> | null,
        vertexData?: SubmeshVertexData | null,
        hasFeatureToUpdate = false
    ) {
        if (!this.enabled) {
            return;
        }
        if (!vertexData) {
            vertexData = this.rendererManager.mesh.rebuildBaseGeometry(meshNode);
        }

        if (vertexData) {
            let newGeometry: Partial<SubmeshVertexData>;
            if (!dataTypes) {
                newGeometry = vertexData;
            } else {
                // If the feature that changed affects aspects of a geometry, reset those aspects so we can recalculate them with
                // this or any other features that affected that aspect
                newGeometry = this.queuedGeometryToReset.get(meshNode._dynamicId) || {};

                // Null means that the vertex data specifically doesn't have those geometries, so they will be cleared
                if (!newGeometry.positions && hasChange(dataTypes, eMeshChangeTypes.VertexPositions)) {
                    newGeometry.positions = vertexData.positions || null;
                }
                if (!newGeometry.normals && hasChange(dataTypes, eMeshChangeTypes.VertexNormals)) {
                    newGeometry.normals = vertexData.normals || null;
                }
                if (!newGeometry.uvs && hasChange(dataTypes, eMeshChangeTypes.VertexUvs)) {
                    newGeometry.uvs = vertexData.uvs || null;
                }
                if (!newGeometry.indices && hasChange(dataTypes, eMeshChangeTypes.VertexIndices)) {
                    newGeometry.indices = vertexData.indices || null;
                }
                if (!newGeometry.submeshData && hasChange(dataTypes, eMeshChangeTypes.Submeshes)) {
                    newGeometry.submeshData = vertexData.submeshData || null;
                }
                if (!newGeometry.colors && hasChange(dataTypes, eMeshChangeTypes.Colors)) {
                    newGeometry.colors = vertexData.colors || null;
                }
            }
            this.queuedGeometryToReset.set(meshNode._dynamicId, newGeometry);

            // If a feature affects a geometry element that is being reset, then it will need to be recalculated
            for (let i = 0; i < meshNode.features.length; i++) {
                const feature = meshNode.features[i];

                // If this geometry hasn't been flagged for recalculation, see if it needs to be
                if (
                    !this.queuedFeaturesToProcess.get(meshNode)?.has(i) &&
                    feature.affects &&
                    featureIsAffected(feature.affects, newGeometry)
                ) {
                    hasFeatureToUpdate = true;
                    this.queueGeometryUpdate(meshNode, i);
                    this.resetMeshGeometry(meshNode, feature.affects, vertexData, hasFeatureToUpdate);
                }
            }

            // If there are no features to be recalculated but we need to reset some mesh geometry, then do that directly.

            /** [RO] This might be a bug in the deferred scene updates - if some features got skipped and there are still some that need to be processed this
             * is called by the mesh renderer directly so this will execute right away and change the geometry before the features are processed. */
            if (!hasFeatureToUpdate && Object.getOwnPropertyNames(newGeometry).length > 0) {
                const existingVertexData = this.rendererManager.mesh.getMeshGeometry(meshNode) || vertexData;
                this.queuedGeometryToReset.delete(meshNode._dynamicId);
                this.applyNewVertexData(meshNode, newGeometry, existingVertexData);
            }
        }
        this.featureOrder.updateFeatureOrder(meshNode);
    }

    transformChanged(meshNode: SpaceNode) {
        if (!this.enabled) {
            return;
        }
        // If the transform on a mesh changes, any features that depend on the transform need to be updated
        for (let i = 0; i < meshNode.features.length; i++) {
            const feature = meshNode.features[i];

            if (feature.affectedBy && feature.affectedBy.has(eMeshChangeTypes.Transform)) {
                this.queueGeometryUpdate(meshNode, i);
            }
        }
    }

    update3dInternal(feature: ITrackedClass<Feature>) {
        if (isTrackedClass(feature)) {
            if (!feature.enabled || feature._suppressed) {
                if (feature.changes.has('enabled') || feature.changes.has('_suppressed')) {
                    this.delete3d(feature, true);
                }

                return;
            }

            const index = this.getFeatureIndexOnParent(feature);
            if (index > -1) {
                this.queueGeometryUpdate(feature._parent!, index);
            }

            // Process all of the feature's non-geometry effects
            const renderer = this.renderers.get(Token.withKey(feature.$type));
            if (!renderer) {
                throw Error('no renderer for feature');
            }
            renderer.update3d(feature);
        }
    }

    update3dGeometry(renderStartTime = 0, node?: MeshNode) {
        if (!node) {
            const process: MeshNode[] = [];
            for (const node of this.queuedFeaturesToProcess.keys()) {
                if (node instanceof MeshNode) {
                    process.push(node);
                    this.update3dGeometry(renderStartTime || performance.now(), node);
                }
            }
            return process;
        }

        if (this.queuedFeaturesToProcess.has(node) && (!(node instanceof CustomMeshNode) || node.geometryLoaded)) {
            const queuedFeatureToProcess = this.queuedFeaturesToProcess.get(node)!;
            const startFeatureIndex = Math.min(...Array.from(queuedFeatureToProcess.values()));

            // There is a known issue here where the VertexData.ExtractFromMesh function does not contain the updated vertices from mesh.updateVerticesData yet.
            // [RO] There is probably some kind of cache that is not updated if VertexData.ExtractFromMesh runs in the same frame
            let vertexDataCurrent = this.processedGeometryMap.get(node._dynamicId);
            if (!vertexDataCurrent) {
                vertexDataCurrent = this.rendererManager.mesh.getMeshGeometry(node) || undefined;
            }

            const vertexDataNew: Partial<SubmeshVertexData> = this.queuedGeometryToReset.has(node._dynamicId)
                ? this.queuedGeometryToReset.get(node._dynamicId)!
                : {};

            // If the whole mesh is being reset, it may be a new mesh without geometry, so we can use the entire vertexDataNew.
            // If the mesh renderer asks the feature renderer for updates, it will not apply the geometry on its own so we have to make sure it happens here.
            // This is done to prevent applying geometry to the mesh multiple times, which is expensive for very large geometry.
            if (!vertexDataCurrent && vertexDataNew instanceof SubmeshVertexData) {
                vertexDataCurrent = vertexDataNew.shallowClone();
            }
            if (!vertexDataCurrent) {
                this.queuedFeaturesToProcess.delete(node);
                this.queuedGeometryToReset.delete(node._dynamicId);
                return;
            }

            for (let i = startFeatureIndex; i < node.features.length; i++) {
                const feature = node.features[i];

                const renderer = this.renderers.get(Token.withKey(feature.$type));
                if (!renderer) {
                    throw Error('no renderer for feature');
                }

                if (!feature.enabled || feature._suppressed) {
                    // If the feature is not enabled, then we don't process it, but we still want to update the feature's geometry cache
                    // Otherwise when the feature is enabled later it might not have the right data.
                    renderer.updateMeshGeometryCache(feature, vertexDataCurrent, vertexDataNew);
                    continue;
                }

                // Always process the features that were modified
                let processThisFeature = queuedFeatureToProcess.has(i);

                // Figure out if a property this feature is affected by was modified this cycle
                if (!processThisFeature && feature.affectedBy) {
                    processThisFeature = featureIsAffected(feature.affectedBy, vertexDataNew);
                }

                if (isTrackedClass(feature)) {
                    if (processThisFeature) {
                        // Process all of the features geometry effects
                        const resultVertexData = renderer.updateMeshGeometry(feature, vertexDataCurrent, vertexDataNew);

                        if (feature.affects) {
                            feature.affects.forEach(affects => {
                                if (affects === eMeshChangeTypes.VertexPositions) {
                                    vertexDataNew.positions = resultVertexData.positions || null;
                                }
                                if (affects === eMeshChangeTypes.VertexNormals) {
                                    vertexDataNew.normals = resultVertexData.normals || null;
                                }
                                if (affects === eMeshChangeTypes.VertexUvs) {
                                    vertexDataNew.uvs = resultVertexData.uvs || null;
                                }
                                if (affects === eMeshChangeTypes.VertexIndices) {
                                    vertexDataNew.indices = resultVertexData.indices || null;
                                }
                                if (affects === eMeshChangeTypes.Submeshes) {
                                    vertexDataNew.submeshData = resultVertexData.submeshData || null;
                                }
                                if (affects === eMeshChangeTypes.Colors) {
                                    vertexDataNew.colors = resultVertexData.colors || null;
                                }
                            });
                        }
                    }
                }
            }

            if (vertexDataNew.submeshData) {
                if (!vertexDataCurrent.positions) {
                    throw Error('Invalid geometry: positions is null');
                }
                const submeshData = vertexDataNew.submeshData.filter(s => s.materialIndex !== 0);
                const baseSubmeshes = calculateBaseSubmeshes(vertexDataCurrent, vertexDataNew, submeshData);
                const verticesCount = (vertexDataNew.positions || vertexDataCurrent.positions).length / 3;
                submeshData.unshift(
                    ...baseSubmeshes.map(bs => ({
                        materialIndex: 0,
                        verticesStart: 0,
                        verticesCount: verticesCount,
                        indexStart: bs.indexStart,
                        indexCount: bs.indexCount,
                    }))
                );
                vertexDataNew.submeshData = submeshData;
            }

            if (Object.getOwnPropertyNames(vertexDataNew).length > 0) {
                const updatedVertexData = this.applyNewVertexData(node, vertexDataNew, vertexDataCurrent);

                // If the geometry was updated, run the post update functions on the feature processors
                for (let i = startFeatureIndex; i < node.features.length; i++) {
                    const feature = node.features[i];

                    const renderer = this.renderers.get(Token.withKey(feature.$type));
                    if (!renderer) {
                        throw Error('no renderer for feature');
                    }
                    if (renderer.postUpdateGeometry) {
                        renderer.postUpdateGeometry(feature, updatedVertexData);
                    }
                }
            }
        }
        this.queuedFeaturesToProcess.delete(node);
        this.queuedGeometryToReset.delete(node._dynamicId);
        this.processedFeatures.set(node._dynamicId, true);
    }

    delete3d(feature: ITrackedClass<Feature>, preserveCache?: boolean) {
        const meshNode = feature._parent;
        if (meshNode && feature.affects) {
            this.resetMeshGeometry(meshNode, feature.affects);
        }

        const renderer = this.renderers.get(Token.withKey(feature.$type));
        if (!renderer) {
            throw Error('no renderer for feature');
        }
        renderer.delete3d(feature, preserveCache);
    }

    dispose() {
        this.queuedFeaturesToProcess.clear();
        this.renderers.forEach(renderer => renderer.dispose());
        super.dispose();
    }

    private queueGeometryUpdate(meshNode: SpaceNode, index?: number) {
        let currentChanges = this.queuedFeaturesToProcess.get(meshNode);
        if (!currentChanges) {
            currentChanges = new Set();
            this.queuedFeaturesToProcess.set(meshNode, currentChanges);
        }

        currentChanges.add(index === undefined ? 0 : index);
    }

    private applyNewVertexData(
        node: MeshNode,
        vertexDataNew: Partial<SubmeshVertexData>,
        vertexDataCurrent: SubmeshVertexData
    ) {
        const mesh = this.viewer.getRendererNode(node) as Mesh;

        SubmeshVertexData.ExtendToRef(vertexDataNew, vertexDataCurrent);

        const invalid = verifyGeometry(vertexDataCurrent);
        if (invalid) {
            console.error(
                `Invalid or corrupted geometry output after feature processing in mesh ${node.name}. The ${invalid} are invalid.`
            );
        } else {
            vertexDataCurrent.applyToMesh(mesh);
            node._polygonCount = mesh.getTotalIndices() / 3;
            node._verticesCount = mesh.getTotalVertices();
            this.viewer.polygonTracker.next();
            this.viewer.warningTracker.next();
        }

        this.rendererManager.mesh.setRenderModeOnMesh(mesh, node._rootScene!);

        this.viewer.bbCache.invalidate(node);
        this.processedGeometryMap.set(node._dynamicId, vertexDataCurrent);
        return vertexDataCurrent;
    }

    private getFeatureIndexOnParent(feature: Feature) {
        if (feature._parent) {
            const index = feature._parent.features.findIndex(f => f._dynamicId === feature._dynamicId);
            if (index > -1) {
                return index;
            }
        }

        return -1;
    }
}

function featureIsAffected(affected: Set<eMeshChangeTypes>, vertexDataNew: Partial<SubmeshVertexData>) {
    let result = false;
    affected.forEach(affectedBy => {
        if (affectedBy === eMeshChangeTypes.VertexPositions) {
            if (vertexDataNew.positions) {
                result = true; // Process the feature if a property it's affected by was changed this cycle
            }
        }
        if (affectedBy === eMeshChangeTypes.VertexNormals) {
            if (vertexDataNew.normals) {
                result = true; // Process the feature if a property it's affected by was changed this cycle
            }
        }
        if (affectedBy === eMeshChangeTypes.VertexUvs) {
            if (vertexDataNew.uvs) {
                result = true; // See above
            }
        }
        if (affectedBy === eMeshChangeTypes.VertexIndices) {
            if (vertexDataNew.indices) {
                result = true; // See above
            }
        }
        if (affectedBy === eMeshChangeTypes.Submeshes) {
            if (vertexDataNew.submeshData) {
                result = true; // See above
            }
        }
        if (affectedBy === eMeshChangeTypes.Colors) {
            if (vertexDataNew.colors) {
                result = true; // See above
            }
        }
    });

    return result;
}

function hasChange(set: Set<eMeshChangeTypes>, changeType: eMeshChangeTypes) {
    return set.has(changeType);
}
